
import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import IngredientsSelector from '../IngredientsSelector';
import Icon from '../Icon';
import Spinner from '../Spinner';

const NAME_STYLES = [
    { id: 'Signature', label: 'Signature', style: { fontFamily: "'Great Vibes', cursive", fontSize: '1.5rem' } },
    { id: 'Bold', label: 'Bold Serif', style: { fontFamily: "'Playfair Display', serif", fontWeight: '700', fontSize: '1rem' } },
    { id: 'Minimal', label: 'MINIMAL', style: { fontFamily: "'Poppins', sans-serif", fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.15em' } },
    { id: 'Gothic', label: 'Old English', style: { fontFamily: "'UnifrakturMaguntia', cursive", fontSize: '1.2rem' } },
    { id: 'ArtDeco', label: 'ART DECO', style: { fontFamily: "'Cinzel', serif", fontWeight: '700', fontSize: '0.75rem' } },
    { id: 'ScriptElegant', label: 'Elegant', style: { fontFamily: "'Dancing Script', cursive", fontWeight: '700', fontSize: '1.2rem' } }
];

const JEWELRY_TYPES = [
    { id: 'Pendant', label: 'Pendant' },
    { id: 'Ring', label: 'Ring' },
    { id: 'Bracelet', label: 'Bracelet' }
];

interface NameplateToolProps {
    onGenerate: (cost: number, quality: 'standard' | 'hd', promptBuilder: (onStatusUpdate?: (msg: string) => void) => Promise<{ prompt: string, images?: any[] }>) => Promise<void>;
    isProcessing: boolean;
    onError: (msg: string) => void;
}

const NameplateTool: React.FC<NameplateToolProps> = ({ onGenerate, isProcessing, onError }) => {
    const { t } = useLanguage();
    
    const [jewelryType, setJewelryType] = useState('Pendant');
    const [textMode, setTextMode] = useState<'single' | 'double'>('single');
    const [nameplateText, setNameplateText] = useState('');
    const [secondNameText, setSecondNameText] = useState('');
    const [nameplateStyle, setNameplateStyle] = useState('Signature');

    const [selectedMetal, setSelectedMetal] = useState('Gold');
    const [selectedFinish, setSelectedFinish] = useState('Polished');
    const [selectedProcess, setSelectedProcess] = useState('None');
    const [selectedStyle, setSelectedStyle] = useState('None');
    const [selectedGemstones, setSelectedGemstones] = useState<string[]>(['None']);

    const handleGenerate = async (quality: 'standard' | 'hd') => {
        if (!nameplateText) {
            onError("Please enter at least one name.");
            return;
        }
        
        // Cost Adjustment for Profit Margin:
        // Standard: 20 Swarnas (~68% Margin)
        // HD: 60 Swarnas (~79% Margin)
        const cost = quality === 'standard' ? 20 : 60;

        await onGenerate(cost, quality, async (onStatusUpdate) => {
            let designStructure = "";
            const cleanName1 = nameplateText.trim();
            const cleanName2 = secondNameText.trim();

            // Detect Regional Scripts (Non-ASCII characters)
            const combinedText = cleanName1 + cleanName2;
            const isRegional = /[^\u0000-\u007F]/.test(combinedText);
            
            let scriptInstruction = "";
            if (isRegional) {
                scriptInstruction = "IMPORTANT: The input text contains Regional Indic Script (e.g., Hindi, Telugu, Tamil, Bengali). Render the 3D metal letters EXACTLY in this native script. Do NOT transliterate to English. Ensure characters are connected for structural integrity.";
            }

            if (textMode === 'double' && cleanName2) {
                designStructure = `Design Configuration: Dual name joined piece. Name 1: "${cleanName1}", Name 2: "${cleanName2}". Connected elegantly.`;
            } else {
                designStructure = `Design Configuration: Single name piece. Name: "${cleanName1}".`;
            }

            const gemstones = selectedGemstones.includes('None') ? '' : `Accents: ${selectedGemstones.join(', ')} studded on the letters or bail.`;
            const styleContext = selectedStyle !== 'None' ? `Technique: ${selectedStyle} style` : '';
            
            // Define Process Aesthetics
            let processDescription = "";
            switch (selectedProcess) {
                case 'Handmade':
                    processDescription = "Manufacturing: Handmade Construction. Letters manually cut from sheet using a jeweler's saw and welded. Edges are filed but retain a soft, manual feel. Matte or hammered finishes preferred.";
                    break;
                case 'CNC/Laser':
                    processDescription = "Manufacturing: Laser Cut & Engraved. Ultra-precise vector cutting with sharp inner corners. Surface features detailed laser engraving art. Perfectly flat planes and industrial precision.";
                    break;
                case 'Navishi':
                    processDescription = "Manufacturing: Navishi (Nakshi) Work. Letters are embossed with deep relief traditional carving. Intricate texture inside the font body.";
                    break;
                case 'CAD/Casting':
                    processDescription = "Manufacturing: 3D CAD Designed & Cast. Letters have significant volume, rounded fillets, and fluid 3D geometry. Allows for complex digital sculpting and precise stone setting.";
                    break;
                default:
                    processDescription = "";
            }

            const prompt = `
            Task: Create a customized ${jewelryType} nameplate design.
            
            Text Content:
            ${designStructure}
            ${scriptInstruction}
            
            Specifications:
            - Typography Aesthetic: ${nameplateStyle} (Adapt this style to the script used).
            - Material: ${selectedMetal} with ${selectedFinish} finish.
            - ${styleContext}
            - ${processDescription}
            - ${gemstones}
            
            Crucial Requirement: The text MUST be legible and form the structural body of the jewelry. The letters should be thick enough to be cast in metal. 3D Metal Render.
            `;
            
            return { prompt, images: [] };
        });
    };

    return (
        <div className="flex flex-col gap-5">
            {/* 1. Composition Section (Top) */}
            <div className="space-y-2">
                 <div className="flex justify-between items-center pl-1">
                    <label className="text-xs uppercase font-black tracking-widest text-slate-400">{t('lblTextMode')}</label>
                    <div className="flex bg-slate-100 dark:bg-white/5 p-0.5 rounded-lg w-32 shadow-inner">
                        <button 
                            onClick={() => setTextMode('single')} 
                            className={`flex-1 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all 
                            ${textMode === 'single' 
                                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' 
                                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
                        >
                            Single
                        </button>
                        <button 
                            onClick={() => setTextMode('double')} 
                            className={`flex-1 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all 
                            ${textMode === 'double' 
                                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' 
                                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
                        >
                            Dual
                        </button>
                    </div>
                 </div>
                 
                 <div className="space-y-2 pt-1">
                    <div className="relative">
                        <input 
                            type="text" 
                            maxLength={20} 
                            value={nameplateText} 
                            onChange={e => setNameplateText(e.target.value)} 
                            className="w-full px-4 py-3 bg-white dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-xl text-sm font-bold text-center outline-none focus:ring-2 focus:ring-apple-gold/20 placeholder-slate-300 dark:placeholder-slate-600 transition-all" 
                            placeholder={textMode === 'single' ? "Name (e.g. Priya / प्रिया)" : "First Name"}
                        />
                    </div>
                    
                    {textMode === 'double' && (
                        <input 
                            type="text" 
                            maxLength={20} 
                            value={secondNameText} 
                            onChange={e => setSecondNameText(e.target.value)} 
                            className="w-full px-4 py-3 bg-white dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-xl text-sm font-bold text-center outline-none focus:ring-2 focus:ring-apple-gold/20 animate-in fade-in slide-in-from-top-1 placeholder-slate-300 dark:placeholder-slate-600 transition-all" 
                            placeholder="Second Name (e.g. Rahul / राहुल)"
                        />
                    )}
                    <p className="text-[10px] text-slate-400 text-center italic">Supports English, Hindi, Telugu, Tamil, Bengali</p>
                </div>
            </div>

            {/* 2. Jewelry Base (Below Composition) */}
            <div className="space-y-2">
                 <label className="text-xs uppercase font-black tracking-widest text-slate-400 pl-1">{t('lblJewelryType')}</label>
                 <div className="grid grid-cols-3 gap-2">
                    {JEWELRY_TYPES.map(type => (
                        <button 
                            key={type.id} 
                            onClick={() => setJewelryType(type.id)} 
                            className={`h-9 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all flex items-center justify-center border
                            ${jewelryType === type.id 
                                ? 'bg-slate-900 text-white dark:bg-white dark:text-black border-transparent shadow-md' 
                                : 'bg-white dark:bg-white/5 border-black/5 dark:border-white/10 text-slate-400 hover:bg-slate-50 dark:hover:bg-white/10'}`}
                        >
                            {type.label}
                        </button>
                    ))}
                 </div>
            </div>

            {/* 3. Style Selection (Compact Buttons) */}
            <div className="space-y-2">
                <label className="text-xs uppercase font-black tracking-widest text-slate-400 pl-1">{t('lblSelectStyle')}</label>
                <div className="grid grid-cols-3 gap-2">
                    {NAME_STYLES.map(style => (
                        <button key={style.id} onClick={() => setNameplateStyle(style.id)} className={`h-10 rounded-lg border flex items-center justify-center transition-all group px-2 ${nameplateStyle === style.id ? 'bg-slate-900 text-white dark:bg-white dark:text-black border-transparent shadow-md scale-[1.02]' : 'bg-white dark:bg-white/5 border-black/5 dark:border-white/10 hover:border-black/10 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'}`}>
                            <span style={style.style} className="leading-none">{style.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            <IngredientsSelector 
                selectedMetal={selectedMetal} setSelectedMetal={setSelectedMetal}
                selectedFinish={selectedFinish} setSelectedFinish={setSelectedFinish}
                selectedProcess={selectedProcess} setSelectedProcess={setSelectedProcess}
                selectedStyle={selectedStyle} setSelectedStyle={setSelectedStyle}
                selectedGemstones={selectedGemstones} setSelectedGemstones={setSelectedGemstones}
            />

            <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-black/5 dark:border-white/5">
                <button 
                    onClick={() => handleGenerate('standard')} 
                    disabled={isProcessing || !nameplateText} 
                    className="flex flex-col items-center justify-center py-4 rounded-2xl font-bold transition-all active:scale-95 bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/10 disabled:opacity-50 group"
                >
                    {isProcessing ? <Spinner /> : (
                        <>
                             <div className="flex items-center gap-2 mb-1">
                                <Icon name="sparkles" className="w-4 h-4 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-white" />
                                <span className="text-sm">Standard</span>
                            </div>
                            <span className="text-xs uppercase font-black tracking-widest opacity-40">20 Swarnas</span>
                        </>
                    )}
                </button>

                <button 
                    onClick={() => handleGenerate('hd')} 
                    disabled={isProcessing || !nameplateText} 
                    className="flex flex-col items-center justify-center py-4 rounded-2xl font-bold transition-all active:scale-95 bg-apple-gold text-black disabled:opacity-50 shadow-lg shadow-apple-gold/20 hover:shadow-apple-gold/40 hover:scale-[1.02]"
                >
                    {isProcessing ? <Spinner /> : (
                        <>
                             <div className="flex items-center gap-2 mb-1">
                                <Icon name="diamond" className="w-4 h-4 text-black" />
                                <span className="text-sm">HD Premium</span>
                            </div>
                            <span className="text-xs uppercase font-black tracking-widest opacity-60">60 Swarnas</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default NameplateTool;
